import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { propertiesApi, favoritesApi, contactRequestsApi, notificationsApi, agenciesApi, formatPrice } from '../../lib/api';
import { PropertyCard } from '../../components/property/PropertyCard';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus, Building2, Trash2, Eye, Heart, BarChart3,
  MessageSquare, Phone, Mail, User, Clock, Home,
  TrendingUp, ChevronRight, MapPin, Bell, Building, Check, Globe
} from 'lucide-react';

// Stat card component
const StatCard = ({ icon: Icon, label, value, color, trend }) => (
  <Card className="border-0 shadow-md hover:shadow-lg transition-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
            <TrendingUp className="w-3 h-3" /> {trend}
          </span>
        )}
      </div>
      <p className="font-manrope text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
    </CardContent>
  </Card>
);

// Contact request card
const ContactRequestCard = ({ request, language }) => {
  const date = new Date(request.created_at);
  const timeAgo = getTimeAgo(date, language);

  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`contact-request-${request.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={request.status === 'new' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'}>
                {request.status === 'new'
                  ? (language === 'fr' ? 'Nouveau' : 'جديد')
                  : (language === 'fr' ? 'Lu' : 'مقروء')}
              </Badge>
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {timeAgo}
              </span>
            </div>

            <p className="font-semibold text-slate-900 mb-1 truncate">{request.name}</p>

            {request.property_title && (
              <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
                <Home className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{request.property_title}</span>
              </p>
            )}

            <div className="flex flex-wrap gap-3 mb-3">
              <a href={`tel:${request.phone}`} className="flex items-center gap-1.5 text-sm text-blue-900 hover:underline">
                <Phone className="w-3.5 h-3.5" /> {request.phone}
              </a>
              <a href={`mailto:${request.email}`} className="flex items-center gap-1.5 text-sm text-blue-900 hover:underline">
                <Mail className="w-3.5 h-3.5" /> {request.email}
              </a>
            </div>

            {request.message && (
              <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 line-clamp-3">
                {request.message}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Time ago helper
function getTimeAgo(date, language) {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (language === 'ar') {
    if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    return `منذ ${diffDays} يوم`;
  }
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays === 1) return 'Hier';
  return `Il y a ${diffDays} jours`;
}

// Property row for dashboard
const PropertyRow = ({ property, language, onDelete, contactCount }) => (
  <Card className="border-0 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={`my-property-${property.id}`}>
    <CardContent className="p-0">
      <div className="flex flex-col sm:flex-row">
        <div className="sm:w-44 h-32 sm:h-auto flex-shrink-0">
          <img
            src={property.images?.[0] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400'}
            alt={property.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-grow p-4 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Badge className={property.status === 'active' ? 'bg-emerald-100 text-emerald-800 border-0' : 'bg-slate-100 text-slate-600 border-0'}>
                {property.status === 'active'
                  ? (language === 'fr' ? 'Active' : 'نشط')
                  : property.status}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {property.transaction_type === 'vente'
                  ? (language === 'fr' ? 'Vente' : 'بيع')
                  : (language === 'fr' ? 'Location' : 'إيجار')}
              </Badge>
            </div>
            <h3 className="font-semibold text-slate-900 mb-0.5 line-clamp-1">{property.title}</h3>
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {property.neighborhood}, {property.city}
            </p>
          </div>

          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-4">
              <p className="font-manrope font-bold text-lg text-blue-900">
                {formatPrice(property.price, property.transaction_type === 'location')}
              </p>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {property.views}</span>
                <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {contactCount}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Link to={`/property/${property.id}`}>
                <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs">
                  <Eye className="w-3.5 h-3.5" />
                  {language === 'fr' ? 'Voir' : 'عرض'}
                </Button>
              </Link>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs text-red-500 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {language === 'fr' ? 'Supprimer l\'annonce ?' : 'حذف الإعلان؟'}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {language === 'fr'
                        ? 'Cette action est irréversible.'
                        : 'هذا الإجراء لا يمكن التراجع عنه.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{language === 'fr' ? 'Annuler' : 'إلغاء'}</AlertDialogCancel>
                    <AlertDialogAction onClick={() => onDelete(property.id)} className="bg-red-500 hover:bg-red-600">
                      {language === 'fr' ? 'Supprimer' : 'حذف'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Notification item
const NotificationItem = ({ notification, language, onMarkRead }) => {
  const date = new Date(notification.created_at);
  const timeAgo = getTimeAgo(date, language);
  return (
    <Card className={`border-0 shadow-sm hover:shadow-md transition-shadow ${!notification.read ? 'ring-1 ring-blue-200 bg-blue-50/30' : ''}`} data-testid={`notification-${notification.id}`}>
      <CardContent className="p-4 flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${!notification.read ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
          <MessageSquare className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${!notification.read ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
            {language === 'ar' ? notification.title_ar : notification.title}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">{language === 'ar' ? notification.message_ar : notification.message}</p>
          <p className="text-[10px] text-slate-300 mt-1">{timeAgo}</p>
        </div>
        {!notification.read && (
          <button onClick={() => onMarkRead(notification.id)} className="text-xs text-blue-600 hover:underline flex items-center gap-1 flex-shrink-0">
            <Check className="w-3 h-3" />
          </button>
        )}
      </CardContent>
    </Card>
  );
};

// Agency profile form
const AgencyProfileForm = ({ language, onSaved }) => {
  const [form, setForm] = useState({ agency_name: '', description: '', description_ar: '', phone: '', email: '', address: '', city: '', website: '' });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    agenciesApi.getMyProfile().then(res => {
      if (res.data) setForm(prev => ({ ...prev, ...res.data }));
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    if (!form.agency_name.trim()) {
      toast.error(language === 'fr' ? 'Le nom de l\'agence est requis' : 'اسم الوكالة مطلوب');
      return;
    }
    setSaving(true);
    try {
      await agenciesApi.updateProfile(form);
      toast.success(language === 'fr' ? 'Profil mis a jour' : 'تم تحديث الملف الشخصي');
      onSaved?.();
    } catch {
      toast.error(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  if (!loaded) return <Skeleton className="h-96 rounded-xl" />;

  return (
    <Card className="border-0 shadow-md" data-testid="agency-profile-form">
      <CardContent className="p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label>{language === 'fr' ? 'Nom de l\'agence *' : 'اسم الوكالة *'}</Label>
            <Input value={form.agency_name} onChange={e => setForm({ ...form, agency_name: e.target.value })} data-testid="agency-name-input" />
          </div>
          <div>
            <Label>{language === 'fr' ? 'Ville' : 'المدينة'}</Label>
            <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="agency-city-input" />
          </div>
          <div>
            <Label>{language === 'fr' ? 'Telephone' : 'الهاتف'}</Label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="agency-phone-input" />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="agency-email-input" />
          </div>
          <div className="md:col-span-2">
            <Label>{language === 'fr' ? 'Adresse' : 'العنوان'}</Label>
            <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="agency-address-input" />
          </div>
          <div className="md:col-span-2">
            <Label>{language === 'fr' ? 'Site web' : 'الموقع الإلكتروني'}</Label>
            <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://" data-testid="agency-website-input" />
          </div>
          <div className="md:col-span-2">
            <Label>{language === 'fr' ? 'Description (FR)' : 'الوصف (فرنسي)'}</Label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} data-testid="agency-desc-input" />
          </div>
          <div className="md:col-span-2">
            <Label>{language === 'fr' ? 'Description (AR)' : 'الوصف (عربي)'}</Label>
            <Textarea value={form.description_ar} onChange={e => setForm({ ...form, description_ar: e.target.value })} rows={3} dir="rtl" data-testid="agency-desc-ar-input" />
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-blue-900 hover:bg-blue-800 rounded-full px-8" data-testid="save-agency-profile">
          {saving ? (language === 'fr' ? 'Sauvegarde...' : 'جاري الحفظ...') : (language === 'fr' ? 'Sauvegarder le profil' : 'حفظ الملف الشخصي')}
        </Button>
      </CardContent>
    </Card>
  );
};

export const DashboardPage = () => {
  const { t, language } = useLanguage();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [contactRequests, setContactRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadDashboard();
  }, [isAuthenticated, authLoading, navigate]);

  const loadDashboard = async () => {
    try {
      const [propsRes, favsRes, contactsRes, notifsRes] = await Promise.all([
        propertiesApi.myProperties().catch(() => ({ data: [] })),
        favoritesApi.list().catch(() => ({ data: [] })),
        contactRequestsApi.list().catch(() => ({ data: [] })),
        notificationsApi.list().catch(() => ({ data: [] })),
      ]);
      setProperties(propsRes.data || []);
      setFavorites(favsRes.data || []);
      setContactRequests(contactsRes.data || []);
      setNotifications(notifsRes.data || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await propertiesApi.delete(id);
      setProperties(properties.filter(p => p.id !== id));
      toast.success(language === 'fr' ? 'Annonce supprimée' : 'تم حذف الإعلان');
    } catch {
      toast.error(language === 'fr' ? 'Erreur lors de la suppression' : 'خطأ أثناء الحذف');
    }
  };

  const totalViews = properties.reduce((sum, p) => sum + (p.views || 0), 0);
  const newRequests = contactRequests.filter(r => r.status === 'new').length;
  const unreadNotifs = notifications.filter(n => !n.read).length;

  const handleMarkRead = async (id) => {
    try {
      await notificationsApi.markRead(id);
      setNotifications(n => n.map(x => x.id === id ? { ...x, read: true } : x));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(n => n.map(x => ({ ...x, read: true })));
    } catch {}
  };

  // Count contacts per property
  const contactCountMap = {};
  contactRequests.forEach(r => {
    contactCountMap[r.property_id] = (contactCountMap[r.property_id] || 0) + 1;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
          </div>
          <Skeleton className="h-10 w-96 mb-4" />
          <div className="space-y-4">
            {[1, 2].map(i => <Skeleton key={i} className="h-36 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-manrope text-2xl md:text-3xl font-bold text-slate-900">
              {language === 'fr' ? `Bonjour, ${user?.full_name?.split(' ')[0] || ''}` : `مرحباً، ${user?.full_name?.split(' ')[0] || ''}`}
            </h1>
            <p className="text-slate-500 mt-1">
              {language === 'fr' ? 'Gérez vos annonces et suivez vos demandes' : 'أدر إعلاناتك وتابع طلباتك'}
            </p>
          </div>
          <Link to="/add-property">
            <Button className="bg-rose-600 hover:bg-rose-700 rounded-full gap-2 px-5" data-testid="dashboard-add-property">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'fr' ? 'Déposer une annonce' : 'نشر إعلان'}</span>
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Building2}
            label={language === 'fr' ? 'Annonces' : 'إعلانات'}
            value={properties.length}
            color="bg-blue-100 text-blue-900"
          />
          <StatCard
            icon={Eye}
            label={language === 'fr' ? 'Vues totales' : 'المشاهدات'}
            value={totalViews}
            color="bg-amber-100 text-amber-800"
          />
          <StatCard
            icon={MessageSquare}
            label={language === 'fr' ? 'Demandes reçues' : 'الطلبات'}
            value={contactRequests.length}
            color="bg-emerald-100 text-emerald-800"
            trend={newRequests > 0 ? `+${newRequests} ${language === 'fr' ? 'nouvelle(s)' : 'جديد'}` : null}
          />
          <StatCard
            icon={Heart}
            label={language === 'fr' ? 'Favoris' : 'المفضلة'}
            value={favorites.length}
            color="bg-rose-100 text-rose-700"
          />
        </div>

        {/* Quick action: Alerts */}
        <div className="flex items-center gap-3 mb-2">
          <Link to="/alerts" className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-900 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors border border-blue-100" data-testid="dashboard-alerts-link">
            <Bell className="w-4 h-4" />
            {language === 'fr' ? 'Gérer mes alertes email' : 'إدارة تنبيهات البريد'}
          </Link>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="properties" className="space-y-6">
          <TabsList className="bg-white shadow-sm border border-slate-100 p-1 rounded-xl w-fit">
            <TabsTrigger value="properties" className="gap-2 rounded-lg data-[state=active]:bg-blue-900 data-[state=active]:text-white" data-testid="tab-my-properties">
              <Building2 className="w-4 h-4" />
              {language === 'fr' ? 'Mes annonces' : 'إعلاناتي'}
              {properties.length > 0 && (
                <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded-full">{properties.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="requests" className="gap-2 rounded-lg data-[state=active]:bg-blue-900 data-[state=active]:text-white" data-testid="tab-requests">
              <MessageSquare className="w-4 h-4" />
              {language === 'fr' ? 'Demandes' : 'الطلبات'}
              {newRequests > 0 && (
                <span className="bg-rose-500 text-white text-xs px-1.5 py-0.5 rounded-full">{newRequests}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="favorites" className="gap-2 rounded-lg data-[state=active]:bg-blue-900 data-[state=active]:text-white" data-testid="tab-favorites">
              <Heart className="w-4 h-4" />
              {language === 'fr' ? 'Favoris' : 'المفضلة'}
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2 rounded-lg data-[state=active]:bg-blue-900 data-[state=active]:text-white" data-testid="tab-notifications">
              <Bell className="w-4 h-4" />
              {language === 'fr' ? 'Notifications' : 'الإشعارات'}
              {unreadNotifs > 0 && (
                <span className="bg-rose-500 text-white text-xs px-1.5 py-0.5 rounded-full">{unreadNotifs}</span>
              )}
            </TabsTrigger>
            {user?.user_type === 'agence' && (
              <TabsTrigger value="agency" className="gap-2 rounded-lg data-[state=active]:bg-blue-900 data-[state=active]:text-white" data-testid="tab-agency-profile">
                <Building className="w-4 h-4" />
                {language === 'fr' ? 'Profil agence' : 'ملف الوكالة'}
              </TabsTrigger>
            )}
          </TabsList>

          {/* My Properties Tab */}
          <TabsContent value="properties" data-testid="properties-tab-content">
            {properties.length === 0 ? (
              <Card className="border-0 shadow-md">
                <CardContent className="py-16 text-center">
                  <Building2 className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {language === 'fr' ? 'Aucune annonce publiée' : 'لا توجد إعلانات منشورة'}
                  </h3>
                  <p className="text-slate-500 mb-6 text-sm">
                    {language === 'fr'
                      ? 'Publiez votre première annonce et recevez des demandes de contact.'
                      : 'انشر إعلانك الأول واستقبل طلبات الاتصال.'}
                  </p>
                  <Link to="/add-property">
                    <Button className="bg-rose-600 hover:bg-rose-700 rounded-full gap-2">
                      <Plus className="w-4 h-4" />
                      {language === 'fr' ? 'Déposer une annonce' : 'نشر إعلان'}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {properties.map(property => (
                  <PropertyRow
                    key={property.id}
                    property={property}
                    language={language}
                    onDelete={handleDelete}
                    contactCount={contactCountMap[property.id] || 0}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contact Requests Tab */}
          <TabsContent value="requests" data-testid="requests-tab-content">
            {contactRequests.length === 0 ? (
              <Card className="border-0 shadow-md">
                <CardContent className="py-16 text-center">
                  <MessageSquare className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {language === 'fr' ? 'Aucune demande reçue' : 'لا توجد طلبات'}
                  </h3>
                  <p className="text-slate-500 text-sm">
                    {language === 'fr'
                      ? 'Les demandes de contact pour vos annonces apparaîtront ici.'
                      : 'ستظهر طلبات الاتصال لإعلاناتك هنا.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {contactRequests.map(request => (
                  <ContactRequestCard key={request.id} request={request} language={language} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites" data-testid="favorites-tab-content">
            {favorites.length === 0 ? (
              <Card className="border-0 shadow-md">
                <CardContent className="py-16 text-center">
                  <Heart className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {language === 'fr' ? 'Aucun favori' : 'لا توجد مفضلات'}
                  </h3>
                  <p className="text-slate-500 mb-6 text-sm">
                    {language === 'fr'
                      ? 'Sauvegardez vos annonces préférées pour les retrouver facilement.'
                      : 'احفظ إعلاناتك المفضلة للعثور عليها بسهولة.'}
                  </p>
                  <Link to="/search">
                    <Button variant="outline" className="rounded-full gap-2">
                      {language === 'fr' ? 'Explorer les annonces' : 'استكشاف الإعلانات'}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map(property => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" data-testid="notifications-tab-content">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">
                {language === 'fr' ? 'Vos notifications' : 'إشعاراتك'}
                {unreadNotifs > 0 && <Badge className="ml-2 bg-rose-100 text-rose-700 border-0">{unreadNotifs} {language === 'fr' ? 'non lues' : 'غير مقروءة'}</Badge>}
              </h3>
              {unreadNotifs > 0 && (
                <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs gap-1" data-testid="mark-all-read-dashboard">
                  <Check className="w-3 h-3" /> {language === 'fr' ? 'Tout marquer lu' : 'تحديد الكل كمقروء'}
                </Button>
              )}
            </div>
            {notifications.length === 0 ? (
              <Card className="border-0 shadow-md">
                <CardContent className="py-16 text-center">
                  <Bell className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {language === 'fr' ? 'Aucune notification' : 'لا توجد إشعارات'}
                  </h3>
                  <p className="text-slate-500 text-sm">
                    {language === 'fr'
                      ? 'Vous recevrez des notifications lorsque quelqu\'un vous contacte.'
                      : 'ستتلقى إشعارات عندما يتصل بك شخص ما.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {notifications.map(n => (
                  <NotificationItem key={n.id} notification={n} language={language} onMarkRead={handleMarkRead} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Agency Profile Tab */}
          {user?.user_type === 'agence' && (
            <TabsContent value="agency" data-testid="agency-tab-content">
              <h3 className="font-semibold text-slate-900 mb-4">
                {language === 'fr' ? 'Profil de votre agence' : 'ملف وكالتك'}
              </h3>
              <AgencyProfileForm language={language} onSaved={loadDashboard} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};
